import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Search, Truck, MapPin } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Input } from "./ui/input";
import { SmartImage } from "./SmartImage";
import { useApp } from "../context/AppContext";
import { useTranslation } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["entrantes", "ramen", "arroz", "tallarines", "carnes", "marisco", "sushi", "poke", "refrescos", "cervezas", "vinos", "copas_vino", "cafes", "cubatas", "chupitos", "cocteles", "postres"];
const DELIVERY_ZONES = ["Moralzarzal", "El Boalo", "Cerceda", "Mataelpino", "Becerril de la Sierra"];

const normalize = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const MenuCard = ({ item, lang, t, onAdd, hasImage }) => {
  const [noodle, setNoodle] = useState("wheat");
  const name = lang === "es" ? item.name_es : item.name_en;
  const desc = lang === "es" ? item.desc_es : item.desc_en;

  const handleClick = () => {
    if (item.has_rice_variant) {
      const suffix = noodle === "rice"
        ? (lang === "es" ? " (fideos de arroz)" : " (rice noodles)")
        : "";
      onAdd({ id: `${item.id}-${noodle}`, name: name + suffix, price: item.price });
    } else {
      onAdd({ id: item.id, name, price: item.price });
    }
  };

  return (
    <article data-testid={`menu-item-${item.id}`} className="menu-card group">
      <div className="relative aspect-square sm:h-52 sm:aspect-auto overflow-hidden">
        <SmartImage
          slot={`dish-${item.id}`}
          hasImage={hasImage(`dish-${item.id}`)}
          alt={name}
          className="w-full h-full"
          imgClassName="menu-card-img w-full h-full object-cover"
        />
        <div className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-[#0A0A0A]/85 text-gold px-2 py-0.5 sm:px-3 sm:py-1 text-xs sm:text-sm font-medium border border-gold/40">
          {item.price.toFixed(2)} €
        </div>
        {item.number && (
          <div className="absolute top-2 left-2 sm:top-3 sm:left-3 bg-[#A91D11]/90 text-white text-[9px] sm:text-[10px] px-1.5 py-0.5 sm:px-2 tracking-[0.12em] sm:tracking-[0.15em]">
            Nº {item.number}
          </div>
        )}
        {/* Round "+" quick-add — only visible on mobile, floats over the image */}
        <button
          data-testid={`add-to-cart-mobile-${item.id}`}
          onClick={handleClick}
          aria-label="Añadir"
          className="sm:hidden absolute bottom-2 right-2 w-11 h-11 rounded-full bg-[#A91D11] text-white flex items-center justify-center shadow-lg active:scale-95 transition-transform"
        >
          <Plus size={18} strokeWidth={2.5} />
        </button>
      </div>
      <div className="p-3 sm:p-5 flex flex-col sm:min-h-[200px]">
        <h3 className="font-serif-q5 text-lg sm:text-2xl text-white leading-tight line-clamp-2 sm:line-clamp-none">{name}</h3>
        <p className="hidden sm:block text-sm text-neutral-400 mt-2 leading-relaxed flex-grow">{desc}</p>
        <p className="sm:hidden text-[11px] text-neutral-400 mt-1 leading-snug line-clamp-2">{desc}</p>

        {item.has_rice_variant && (
          <div data-testid={`noodle-toggle-${item.id}`} className="mt-3 flex items-center gap-1.5 sm:gap-2 flex-wrap">
            <span className="hidden sm:inline text-[10px] uppercase tracking-[0.18em] text-neutral-500">{t.menu.noodleType}:</span>
            <button
              type="button"
              data-testid={`noodle-wheat-${item.id}`}
              onClick={() => setNoodle("wheat")}
              className={`text-[10px] sm:text-[11px] px-2 py-0.5 sm:px-2.5 sm:py-1 border transition-colors ${noodle === "wheat" ? "border-gold text-gold bg-gold/10" : "border-q5 text-neutral-400 hover:border-gold/60"}`}
            >
              {t.menu.noodleWheat}
            </button>
            <button
              type="button"
              data-testid={`noodle-rice-${item.id}`}
              onClick={() => setNoodle("rice")}
              className={`text-[10px] sm:text-[11px] px-2 py-0.5 sm:px-2.5 sm:py-1 border transition-colors ${noodle === "rice" ? "border-gold text-gold bg-gold/10" : "border-q5 text-neutral-400 hover:border-gold/60"}`}
            >
              {t.menu.noodleRice}
            </button>
          </div>
        )}

        <button
          data-testid={`add-to-cart-${item.id}`}
          onClick={handleClick}
          className="hidden sm:inline-flex mt-4 items-center justify-center gap-2 text-xs uppercase tracking-[0.2em] text-gold hover:text-white border-t border-q5 pt-3 transition-colors"
        >
          <Plus size={14} /> {t.menu.addToCart}
        </button>
      </div>
    </article>
  );
};

export const Menu = () => {
  const { lang, addToCart, hasImage } = useApp();
  const t = useTranslation(lang);
  const [items, setItems] = useState([]);
  const [active, setActive] = useState("entrantes");
  const [query, setQuery] = useState("");

  useEffect(() => {
    axios.get(`${API}/menu`).then((r) => setItems(r.data.items || [])).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const q = normalize(query.trim());
    // Global search: when the user types anything, search across ALL categories
    // and ignore the active tab. Otherwise keep the current tab.
    if (q) {
      return items.filter((i) =>
        normalize(lang === "es" ? i.name_es : i.name_en).includes(q) ||
        normalize(lang === "es" ? i.desc_es : (i.desc_en || "")).includes(q)
      );
    }
    return items.filter((i) => i.category === active);
  }, [items, active, query, lang]);

  const handleAdd = (cartItem) => {
    addToCart(cartItem);
    toast.success(t.menu.added, {
      description: `${cartItem.name} · ${cartItem.price.toFixed(2)} €`,
    });
  };

  return (
    <section id="menu" data-testid="menu-section" className="relative py-16 md:py-24 lg:py-36 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-10">
          <span className="overline">{t.menu.overline}</span>
          <h2 className="font-serif-q5 text-4xl md:text-5xl mt-3 text-white">{t.menu.title}</h2>
          <p className="text-neutral-400 mt-5 max-w-xl mx-auto">{t.menu.subtitle}</p>
        </div>

        {/* Delivery-zones notice — elegant, right above the search & tabs. */}
        <motion.div
          data-testid="delivery-zones-inline"
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-4xl mx-auto mb-8 border border-gold/30 bg-gradient-to-r from-transparent via-gold/[0.04] to-transparent px-5 py-4"
        >
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 border border-gold/60 flex items-center justify-center flex-shrink-0">
              <Truck size={16} className="text-gold" />
            </div>
            <div className="flex-1">
              <p className="overline text-[10px] mb-1.5">{lang === "es" ? "Reparto a domicilio" : "Home delivery"}</p>
              <p className="text-sm text-neutral-300 leading-relaxed mb-2">
                {lang === "es"
                  ? "Repartimos en estas localidades cercanas y alrededores. Si la tuya no aparece, llámanos y te confirmamos disponibilidad."
                  : "We deliver to these nearby towns and surrounding areas. If yours isn't listed, call us to confirm availability."}
              </p>
              <ul className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] uppercase tracking-[0.14em] text-gold">
                {DELIVERY_ZONES.map((z, i) => (
                  <li key={z} className="flex items-center gap-1" data-testid={`menu-zone-${i}`}>
                    <MapPin size={9} className="opacity-60" /> {z}
                  </li>
                ))}
                <li className="flex items-center gap-1 text-gold/70 italic normal-case tracking-normal" data-testid="menu-zone-more">
                  · y otras localidades cercanas
                </li>
              </ul>
              <p
                data-testid="drinks-caveat"
                className="mt-3 pt-3 border-t border-gold/20 text-[11px] text-neutral-400 italic leading-relaxed"
              >
                {lang === "es"
                  ? "Nota: algunas bebidas (cócteles servidos en copa, chupitos, jarras y cafés) solo se sirven en el restaurante y no están disponibles para recoger ni para envío a domicilio."
                  : "Note: some drinks (cocktails served in a glass, shots, jugs and coffees) are served in-restaurant only — not available for pickup or delivery."}
              </p>
            </div>
          </div>
        </motion.div>

        <div className="flex justify-center mb-8">
          <div className="relative w-full max-w-md">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gold pointer-events-none" />
            <Input
              data-testid="menu-search"
              placeholder={lang === "es" ? "Buscar en toda la carta..." : "Search the full menu..."}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="bg-transparent border-q5 h-11 pl-11 focus-visible:ring-gold focus-visible:ring-offset-0 rounded-sm"
            />
          </div>
        </div>

        <Tabs value={active} onValueChange={setActive} className="w-full">
          <div className="sticky top-[99px] md:top-[80px] z-30 -mx-6 md:-mx-12 px-4 md:px-12 py-2 md:py-3 mb-6 md:mb-8 bg-[#0A0A0A]/95 backdrop-blur-md border-y border-q5">
            <TabsList
              data-testid="menu-tabs-list"
              className="flex justify-start gap-2 bg-transparent h-auto p-0 overflow-x-auto snap-x snap-mandatory scrollbar-hide -mx-1 px-1"
            >
              {CATEGORIES.map((c) => (
                <TabsTrigger
                  key={c}
                  value={c}
                  data-testid={`menu-tab-${c}`}
                  className="flex-shrink-0 snap-start data-[state=active]:bg-[#A91D11] data-[state=active]:text-white data-[state=active]:border-[#A91D11] bg-transparent border border-q5 text-neutral-300 text-xs uppercase tracking-[0.18em] px-5 py-2.5 rounded-sm hover:border-gold transition-colors whitespace-nowrap"
                >
                  {t.menu.categories[c]}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {CATEGORIES.map((c) => (
            <TabsContent key={c} value={c} className="mt-0">
              <AnimatePresence mode="wait">
                <motion.div
                  key={c + query}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.35 }}
                  className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6"
                >
                  {filtered.map((item) => (
                    <MenuCard key={item.id} item={item} lang={lang} t={t} onAdd={handleAdd} hasImage={hasImage} />
                  ))}
                </motion.div>
              </AnimatePresence>
              {filtered.length === 0 && (
                <p className="text-center text-neutral-500 py-12">
                  {lang === "es" ? "Sin resultados" : "No results"}
                </p>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
};
